// Player versions Bridge learned from the DJ's off-track picks (ITER-181) — server-written, the DJ reads and removes

import { collection, deleteDoc, doc, getDocs, type DocumentData } from 'firebase/firestore';
import { db } from './firebase';
import type { LinkProvider } from '../utils/songLinks';

const LINK_PROVIDERS: LinkProvider[] = ['spotify', 'tidal', 'apple'];

export interface BridgeAlias {
  aliasId: string;
  heardTitle: string;
  heardArtist: string;
  songKey: string;
  title: string;
  artist: string;
  provider: LinkProvider;
  eventId: string;
  requestId: string;
  updatedAt: string | null;
}

function isoOrNull(value: unknown): string | null {
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return typeof value === 'string' ? value : null;
}

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

export function docToBridgeAlias(id: string, data: DocumentData): BridgeAlias | null {
  if (!nonEmpty(data.heardTitle) || typeof data.heardArtist !== 'string') return null;
  if (!nonEmpty(data.songKey) || !nonEmpty(data.title) || typeof data.artist !== 'string') return null;
  if (!LINK_PROVIDERS.includes(data.provider) || !nonEmpty(data.eventId) || !nonEmpty(data.requestId)) return null;
  return {
    aliasId: id,
    heardTitle: data.heardTitle,
    heardArtist: data.heardArtist,
    songKey: data.songKey,
    title: data.title,
    artist: data.artist,
    provider: data.provider,
    eventId: data.eventId,
    requestId: data.requestId,
    updatedAt: isoOrNull(data.updatedAt),
  };
}

export async function getMyBridgeAliases(uid: string): Promise<BridgeAlias[]> {
  const snap = await getDocs(collection(db, 'users', uid, 'bridgeAliases'));
  return snap.docs
    .map((snapshot) => docToBridgeAlias(snapshot.id, snapshot.data()))
    .filter((alias): alias is BridgeAlias => alias !== null)
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
}

export async function deleteBridgeAlias(uid: string, aliasId: string): Promise<void> {
  await deleteDoc(doc(db, 'users', uid, 'bridgeAliases', aliasId));
}
